<script lang="ts">
  import { onDestroy, onMount, untrack } from "svelte"
  import type { Snippet } from "svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { ApiError } from "../../../../../../../api/kehrblatt/client"
  import {
    cancelScheduleEvent,
    fetchScheduleEvent,
    updateScheduleEvent,
  } from "../../../../../../../api/kehrblatt/schedule-events"
  import { fetchProperties } from "../../../../../../../api/kehrblatt/property-registry/properties"
  import {
    buildWorkOrderById,
    type EmployeeOption,
    type WorkingHoursByUser,
  } from "../../../../../../../_shared/_logic/form-state"
  import { scheduleEventHasContactOrder } from "../../../../../../../_shared/_logic/outreach-presentation"
  import ScheduleEventActionButtons from "./_modules/schedule-event-action-buttons"
  import AppointmentContactSummary from "./_modules/appointment-contact-summary"
  import ScheduleEventCommunicationSection from "./_modules/schedule-event-communication-section"
  import ScheduleEventLifecycleHistoryOverlay from "./_modules/schedule-event-lifecycle-history-overlay"
  import ScheduleEventLifecycleTimeline from "./_shared/_modules/schedule-event-lifecycle-timeline"
  import AppointmentConfirmDialog from "../appointment-confirm-dialog"
  import AppointmentRescheduleDialog from "../appointment-reschedule-dialog"
  import {
    executeSingleConfirmAppointment,
    executeSingleRescheduleAppointment,
  } from "../../_logic/single-appointment-actions"
  import { deleteEventAction } from "./schedule-event-actions"
  import { runScheduleEventActionWithValidation } from "../../_logic/run-schedule-event-action-with-validation"
  import { ScheduleEventHeader } from "./_modules/schedule-event-header"
  import { buildScheduleEventHeaderActions } from "./_shared/_logic/header-actions"
  import EventDetailsSection from "./_modules/event-details-section"
  import type {
    LoadScheduleEventReadOverlayDetails,
    ScheduleEventReadOverlayDetailContext,
  } from "../../_logic/read-contract"

  type OverlayHostProps = {
    onremoveOverlay?: () => void
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId: string
    event: ScheduleEvent
    employees: EmployeeOption[]
    workOrders: WorkOrder[]
    properties?: Property[]
    customers?: Customer[]
    contactPoints?: ContactPoint[]
    scheduleEvents?: ScheduleEvent[]
    contactOrders?: ContactOrder[]
    workingHoursByUser?: WorkingHoursByUser
    forceReadOnly?: boolean
    loadDetailContext?: LoadScheduleEventReadOverlayDetails | null
    onSaved?: (event: ScheduleEvent) => Promise<void> | void
    onDeleted?: (id: string) => Promise<void> | void
    onRefresh?: () => Promise<void> | void
  }

  let {
    organizationId,
    event,
    employees = [],
    workOrders = [],
    properties = [],
    customers = [],
    contactPoints = [],
    scheduleEvents = [],
    contactOrders = [],
    workingHoursByUser = {},
    forceReadOnly = false,
    loadDetailContext = null,
    onSaved = null,
    onDeleted = null,
    onRefresh = null,
    onremoveOverlay = null,
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  const initialDetail = untrack(() => ({
    event,
    title: event.title ?? "",
    description: event.description ?? "",
  }))

  let detailEvent = $state<ScheduleEvent>(initialDetail.event)
  let loadedWorkOrders = $state<WorkOrder[]>([])
  let loadedProperties = $state<Property[]>([])
  let loadedCustomers = $state<Customer[]>([])
  let loadedContactPoints = $state<ContactPoint[]>([])
  let loadedScheduleEvents = $state<ScheduleEvent[]>([])
  let loadedContactOrders = $state<ContactOrder[]>([])
  let title = $state(initialDetail.title)
  let description = $state(initialDetail.description)
  let detailContextLoading = $state(untrack(() => Boolean(loadDetailContext)))
  let detailContextError = $state<string | null>(null)

  let saving = $state(false)
  let deleting = $state(false)
  let confirming = $state(false)
  let canceling = $state(false)
  let initiatingReschedule = $state(false)
  let confirmDialogOpen = $state(false)
  let confirmDialogError = $state<string | null>(null)
  let rescheduleDialogOpen = $state(false)
  let rescheduleDialogError = $state<string | null>(null)

  const resolveErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof ApiError && error.message?.trim()) return error.message.trim()

    if (error instanceof Error && error.message?.trim()) return error.message.trim()

    return fallback
  }

  const mergeById = <T extends { id?: string | null }>(base: T[], extra: T[]): T[] => {
    const byId = new Map<string, T>()
    for (const entry of base) {
      if (entry?.id) byId.set(entry.id, entry)
    }
    for (const entry of extra) {
      if (entry?.id) byId.set(entry.id, entry)
    }

    return Array.from(byId.values())
  }

  const effectiveWorkOrders = $derived(mergeById(workOrders, loadedWorkOrders))
  const propertyList = $derived(mergeById(properties, loadedProperties))
  const effectiveCustomers = $derived(mergeById(customers, loadedCustomers))
  const effectiveContactPoints = $derived(mergeById(contactPoints, loadedContactPoints))
  const effectiveScheduleEvents = $derived(mergeById(scheduleEvents, loadedScheduleEvents))
  const effectiveContactOrders = $derived(mergeById(contactOrders, loadedContactOrders))
  const workOrderById = $derived(buildWorkOrderById(effectiveWorkOrders))
  const workOrder = $derived(
    detailEvent.work_order_id ? workOrderById[detailEvent.work_order_id] : null,
  )
  const property = $derived(
    propertyList.find(
      (entry) => entry.id === (detailEvent.property_id ?? workOrder?.property_id ?? null),
    ) ?? null,
  )
  const isAppointment = $derived(detailEvent.event_type === "APPOINTMENT")
  const startInPast = $derived(new Date(detailEvent.start_at).getTime() < Date.now())
  const actionLocked = $derived(
    Boolean(forceReadOnly || detailContextLoading || detailContextError),
  )
  const hasLoadedCurrentWorkOrder = $derived(
    !detailEvent.work_order_id ||
      loadedWorkOrders.some((entry) => entry.id === detailEvent.work_order_id),
  )
  const appointmentDetailSectionsReady = $derived(
    Boolean(
      isAppointment &&
      !detailContextLoading &&
      !detailContextError &&
      (!loadDetailContext || hasLoadedCurrentWorkOrder),
    ),
  )
  const detailsDirty = $derived(
    Boolean(
      (!isAppointment && title.trim() !== (detailEvent.title ?? "")) ||
      description.trim() !== (detailEvent.description ?? ""),
    ),
  )
  const detailsLocked = $derived(actionLocked)
  const canSubmit = $derived(Boolean(!detailsLocked && detailsDirty && !saving))
  const canConfirmAppointment = $derived(
    Boolean(
      detailEvent.id &&
      isAppointment &&
      !actionLocked &&
      ["RESERVED", "ANNOUNCED"].includes(detailEvent.status ?? ""),
    ),
  )
  const canMarkNoShow = $derived(
    Boolean(detailEvent.id && isAppointment && detailEvent.status === "COMPLETED" && !actionLocked),
  )
  const hasAnyContactOrder = $derived(
    scheduleEventHasContactOrder(detailEvent, effectiveContactOrders),
  )
  const showRescheduleAction = $derived(
    Boolean(
      detailEvent.id &&
      isAppointment &&
      !actionLocked &&
      ["RESERVED", "ANNOUNCED", "CONFIRMED", "COMPLETED"].includes(detailEvent.status ?? ""),
    ),
  )
  const syncDraftFromEvent = (target: ScheduleEvent) => {
    title = target.title ?? ""
    description = target.description ?? ""
  }

  onMount(() => {
    onshowOverlay?.()
    setActionButtonSnippet?.(actionButtons)
    syncDraftFromEvent(detailEvent)
    void loadInitialDetailContext()
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })

  const loadDetail = async () => {
    if (!event?.id) return

    try {
      const loaded = await fetchScheduleEvent(event.id)
      detailEvent = loaded
      syncDraftFromEvent(loaded)
    } catch (err) {
      console.error("schedule_event_detail_load_failed", err)
      newNotification({
        class: "error",
        html: resolveErrorMessage(err, "Termin konnte nicht vollständig geladen werden."),
      })
    }
  }

  const applyDetailContext = (context: ScheduleEventReadOverlayDetailContext | null) => {
    if (!context) return

    if (context.event) {
      detailEvent = context.event
      syncDraftFromEvent(context.event)
    }

    if (context.workOrders) loadedWorkOrders = context.workOrders

    if (context.properties) loadedProperties = context.properties

    if (context.customers) loadedCustomers = context.customers

    if (context.contactPoints) loadedContactPoints = context.contactPoints

    if (context.scheduleEvents) loadedScheduleEvents = context.scheduleEvents

    if (context.contactOrders) loadedContactOrders = context.contactOrders
  }

  const loadLazyDetailContext = async () => {
    if (!loadDetailContext) return

    detailContextLoading = true
    detailContextError = null
    try {
      applyDetailContext(await loadDetailContext())
    } catch (err) {
      console.error("schedule_event_lazy_detail_context_load_failed", err)
      detailContextError = resolveErrorMessage(
        err,
        "Auftragsdetails konnten nicht vollständig geladen werden.",
      )
    } finally {
      detailContextLoading = false
    }
  }

  const loadInitialDetailContext = async () => {
    if (loadDetailContext) {
      await loadLazyDetailContext()
    } else {
      await loadDetail()
    }

    await loadPropertyIfNeeded()
  }

  const loadPropertyIfNeeded = async () => {
    const propertyId = detailEvent.property_id ?? workOrder?.property_id ?? null

    if (!organizationId || !propertyId || propertyList.some((entry) => entry.id === propertyId))
      return

    try {
      loadedProperties = await fetchProperties({
        organizationId,
        propertyIds: [propertyId],
        limit: 1,
      })
    } catch (err) {
      console.error("schedule_event_property_load_failed", err)
    }
  }

  const propagateChange = async (updated?: ScheduleEvent | null) => {
    if (updated) {
      detailEvent = updated
      syncDraftFromEvent(updated)
      await onSaved?.(updated)

      return
    }

    await onRefresh?.()
  }

  const saveDetails = async () => {
    if (!detailEvent.id || saving || !canSubmit) return

    saving = true
    try {
      const payload: ScheduleEventUpdatePayload = {
        organization_id: organizationId,
        description: description.trim() || null,
      }

      if (!isAppointment) {
        payload.title = title.trim() || null
      }

      const result = await runScheduleEventActionWithValidation({
        validationRequest: {
          operation: "UPDATE",
          surface: "DETAIL_FORM",
          event_id: detailEvent.id,
          update_payload: payload,
        },
        executeDirect: () => updateScheduleEvent(detailEvent.id, payload),
      })

      if (result.status === "canceled") return

      await propagateChange(result.value)
      newNotification({ class: "success", html: "Eintrag aktualisiert." })
    } catch (err) {
      console.error("schedule_event_details_save_failed", err)
      newNotification({
        class: "error",
        html: resolveErrorMessage(err, "Eintrag konnte nicht gespeichert werden."),
      })
    } finally {
      saving = false
    }
  }

  const confirmEvent = () => {
    if (!detailEvent.id || !canConfirmAppointment || confirming) return

    confirmDialogError = null
    confirmDialogOpen = true
  }

  const closeConfirmDialog = () => {
    if (confirming) return

    confirmDialogOpen = false
    confirmDialogError = null
  }

  const submitConfirmEvent = async ({
    sendConfirmationEmail,
  }: {
    sendConfirmationEmail: boolean
  }) => {
    if (!detailEvent.id || confirming) return

    confirming = true
    confirmDialogError = null
    try {
      const response = await executeSingleConfirmAppointment({
        eventId: detailEvent.id,
        sendConfirmationEmail,
      })
      await propagateChange(response.event)
      confirmDialogOpen = false
      onremoveOverlay?.()
    } catch (err) {
      console.error("schedule_event_single_confirm_failed", err)
      const message = resolveErrorMessage(err, "Termin konnte nicht bestätigt werden.")
      confirmDialogError = message
      newNotification({ class: "error", html: message })
    } finally {
      confirming = false
    }
  }

  const startReschedule = () => {
    if (!detailEvent.id || initiatingReschedule) return

    rescheduleDialogError = null
    rescheduleDialogOpen = true
  }

  const closeRescheduleDialog = () => {
    if (initiatingReschedule) return

    rescheduleDialogOpen = false
    rescheduleDialogError = null
  }

  const submitRescheduleEvent = async ({
    updatePayload,
    sendConfirmationEmail,
  }: {
    updatePayload: ScheduleEventUpdatePayload
    sendConfirmationEmail: boolean
  }) => {
    if (!detailEvent.id || initiatingReschedule) return

    initiatingReschedule = true
    rescheduleDialogError = null
    try {
      const result = await executeSingleRescheduleAppointment({
        eventId: detailEvent.id,
        updatePayload,
        sendConfirmationEmail,
      })

      if (result.status === "canceled") return

      await propagateChange(result.value.created_event)
      rescheduleDialogOpen = false
      onremoveOverlay?.()
    } catch (err) {
      console.error("schedule_event_single_reschedule_failed", err)
      const message = resolveErrorMessage(err, "Termin konnte nicht verschoben werden.")
      rescheduleDialogError = message
      newNotification({ class: "error", html: message })
    } finally {
      initiatingReschedule = false
    }
  }

  const handleDelete = () => {
    void deleteEventAction({
      event: detailEvent,
      isDeleting: () => deleting,
      setDeleting: (value) => (deleting = value),
      onDeleted,
      onremoveOverlay,
      customerCommunicationMode: null,
      reschedule: false,
      requireApproval: true,
      copy: null,
    })
  }

  const markNoShow = async () => {
    if (!detailEvent.id || !canMarkNoShow || canceling) return

    canceling = true
    try {
      const updated = await cancelScheduleEvent(detailEvent.id, { target_status: "NOSHOW" })
      await propagateChange(updated)
      newNotification({ class: "success", html: "Termin als nicht stattgefunden markiert." })
      onremoveOverlay?.()
    } catch (err) {
      console.error("schedule_event_mark_no_show_failed", err)
      newNotification({
        class: "error",
        html: resolveErrorMessage(
          err,
          "Termin konnte nicht als nicht stattgefunden markiert werden.",
        ),
      })
    } finally {
      canceling = false
    }
  }

  const handleContactOrderCreated = async (updatedEvent: ScheduleEvent) => {
    await propagateChange(updatedEvent)
    await onRefresh?.()
  }

  const headerActions = $derived(
    buildScheduleEventHeaderActions({
      event: detailEvent,
      forceReadOnly: actionLocked,
      isAppointment,
      showSaveAction: detailsDirty && !detailsLocked,
      canConfirmAppointment,
      showRescheduleAction,
      canMarkNoShow,
      saving,
      deleting,
      initiatingReschedule,
      canceling,
      confirming,
      onDelete: handleDelete,
      onStartReschedule: startReschedule,
      onMarkNoShow: markNoShow,
    }),
  )
</script>

{#snippet actionButtons()}
  <ScheduleEventActionButtons
    primaryOnly
    event={detailEvent}
    forceReadOnly={actionLocked}
    {isAppointment}
    {showRescheduleAction}
    showSaveAction={detailsDirty && !detailsLocked}
    {canMarkNoShow}
    {canConfirmAppointment}
    contactOrderBusy={false}
    contactOrdersLoading={false}
    contactOrderLabel="Ankündigungsplan erstellen"
    contactOrderTooltip={null}
    showContactOrderAction={false}
    canStartContactOrder={false}
    {saving}
    {deleting}
    {initiatingReschedule}
    {confirming}
    {canceling}
    {canSubmit}
    onDelete={handleDelete}
    onStartReschedule={startReschedule}
    onMarkNoShow={markNoShow}
    onConfirm={confirmEvent}
    onStartContactOrder={() => undefined}
    onSubmit={saveDetails}
  />
{/snippet}

<ScheduleEventHeader
  event={detailEvent}
  {employees}
  {workOrder}
  {property}
  actions={headerActions}
/>
<div class="record-overlay-content schedule-event-read-overlay form dark">
  {#if detailEvent.customer_note}
    <section class="customer-note" aria-label="Kundenhinweis">
      <h4>Kundenhinweis</h4>
      <p>{detailEvent.customer_note}</p>
    </section>
  {/if}
  {#if detailContextLoading}
    <div class="detail-loading" role="status" aria-live="polite">
      <span class="detail-loading__spinner"></span>
      <span>Auftragsdetails werden geladen...</span>
    </div>
  {:else if detailContextError}
    <p class="detail-loading detail-loading--error">{detailContextError}</p>
  {/if}
  <EventDetailsSection event={detailEvent} bind:title bind:description disabled={detailsLocked} />
  {#if appointmentDetailSectionsReady}
    <AppointmentContactSummary
      event={detailEvent}
      {workOrder}
      customers={effectiveCustomers}
      contactPoints={effectiveContactPoints}
    />
    <ScheduleEventCommunicationSection
      {organizationId}
      event={detailEvent}
      {workOrder}
      contactPoints={effectiveContactPoints}
      customers={effectiveCustomers}
      {forceReadOnly}
      onCreated={handleContactOrderCreated}
    />
  {/if}
  {#if detailEvent.id && isAppointment && !detailContextError}
    <ScheduleEventLifecycleTimeline
      event={detailEvent}
      onShowAll={() => {
        openOverlay({
          title: "Terminverlauf",
          content: ScheduleEventLifecycleHistoryOverlay,
          closeOnParentOpen: false,
          isDark: true,
          properties: { event: detailEvent },
        })
      }}
    />
  {/if}
  {#if forceReadOnly}
    <p class="read-only-note">Dieser Eintrag ist schreibgeschützt.</p>
  {:else if startInPast && isAppointment}
    <p class="read-only-note">Vergangene Terminzeiten können nicht mehr umgeplant werden.</p>
  {/if}
</div>

{#if confirmDialogOpen}
  <AppointmentConfirmDialog
    pending={confirming}
    error={confirmDialogError}
    onConfirm={submitConfirmEvent}
    onClose={closeConfirmDialog}
  />
{/if}

{#if rescheduleDialogOpen}
  <AppointmentRescheduleDialog
    event={detailEvent}
    {workOrder}
    {employees}
    scheduleEvents={effectiveScheduleEvents}
    {workingHoursByUser}
    pending={initiatingReschedule}
    error={rescheduleDialogError}
    onReschedule={submitRescheduleEvent}
    onClose={closeRescheduleDialog}
  />
{/if}

<style>
  .schedule-event-read-overlay {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
  }

  .read-only-note {
    margin: 0.25rem 0 0;
    color: var(--text-60);
    line-height: 1.35;
  }

  .customer-note h4 {
    margin: 0 0 4px;
    font-size: 14px;
  }

  .customer-note p {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .detail-loading {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-height: 2.2rem;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--border-60);
    color: var(--text-60);
    font-size: 0.88rem;
    line-height: 1.35;
  }

  .detail-loading--error {
    color: var(--danger-100, var(--error-100, var(--text-100)));
  }

  .detail-loading__spinner {
    width: 0.8rem;
    height: 0.8rem;
    flex: 0 0 auto;
    border: 2px solid var(--border-60);
    border-top-color: var(--accent-100, var(--green-100));
    border-radius: 999px;
    animation: detail-loading-spin 0.75s linear infinite;
  }

  @keyframes detail-loading-spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
